export const PEOPLE_ROLES = {
  AGENT: "agent",
  ADMIN: "admin",
  BILLING_OWNER: "admin_with_billing",
};

export function normalizePeopleRole(roleName) {
  return (roleName || "").toLowerCase();
}

export function isAdminRole(roleName) {
  const role = normalizePeopleRole(roleName);
  return role === PEOPLE_ROLES.ADMIN || role === PEOPLE_ROLES.BILLING_OWNER;
}

export function isBillingOwnerRole(roleName) {
  return normalizePeopleRole(roleName) === PEOPLE_ROLES.BILLING_OWNER;
}

export function isAgentRole(roleName) {
  return normalizePeopleRole(roleName) === PEOPLE_ROLES.AGENT;
}

export function isProtectedBillingOwner(targetRoleName) {
  return isBillingOwnerRole(targetRoleName);
}

export function peopleIdsMatch(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export function getCurrentUserFromState(state) {
  const user = state?.loginToken?.response?.user || state?.loginTokenCoUser?.response?.user;
  if (!user) return null;
  return {
    id: user.id || user._id,
    roleName: user.role_name || "",
  };
}

/** Customers */
export function canCreateCustomer(roleName) {
  return isAdminRole(roleName) || isAgentRole(roleName);
}

export function canEditCustomer(roleName) {
  return isAdminRole(roleName) || isAgentRole(roleName);
}

export function canChangeCustomerStatus(roleName) {
  return isAdminRole(roleName) || isAgentRole(roleName);
}

export function canDeleteCustomer(roleName) {
  return isAdminRole(roleName);
}

export function canBanCustomer(roleName) {
  return isAdminRole(roleName) || isAgentRole(roleName);
}

export function canUnbanCustomer(roleName) {
  return isAdminRole(roleName);
}

export function isCustomerBanAdminRole(roleName) {
  return isAdminRole(roleName);
}

/** Teams */
export function canCreateTeam(roleName) {
  return isAdminRole(roleName);
}

export function canEditTeam(roleName) {
  return isAdminRole(roleName);
}

export function canManageTeamMembers(roleName) {
  return isAdminRole(roleName);
}

export function canArchiveTeam(roleName) {
  return isAdminRole(roleName);
}

export function canDeleteTeam(roleName) {
  return isAdminRole(roleName);
}

export function canBulkAssignTeamGroup(roleName) {
  return isAdminRole(roleName);
}

/** Agents */
export function canInviteAgent(roleName) {
  return isAdminRole(roleName);
}

export function canEditAgent(roleName, currentUserId, targetAgentId, targetRoleName) {
  if (peopleIdsMatch(currentUserId, targetAgentId)) return true;
  if (isProtectedBillingOwner(targetRoleName)) return false;
  return isAdminRole(roleName);
}

export function canChangeAgentStatus(roleName, currentUserId, targetAgentId, targetRoleName) {
  if (peopleIdsMatch(currentUserId, targetAgentId)) return true;
  if (isProtectedBillingOwner(targetRoleName)) return false;
  return isAdminRole(roleName);
}

export function canAssignAgentTeamsGroups(roleName) {
  return isAdminRole(roleName);
}

export function canSuspendAgent(roleName, targetRoleName) {
  if (isProtectedBillingOwner(targetRoleName)) return false;
  return isAdminRole(roleName);
}

export function canDeleteAgent(roleName, targetRoleName) {
  if (isProtectedBillingOwner(targetRoleName)) return false;
  return isAdminRole(roleName);
}

export function canActivateAgent(roleName, targetRoleName) {
  return canSuspendAgent(roleName, targetRoleName);
}

export function canBulkManageAgents(roleName) {
  return isAdminRole(roleName);
}

/** Groups */
export function canViewGroups() {
  return true;
}

export function canCreateGroup(roleName) {
  return isAdminRole(roleName);
}

export function canEditGroup(roleName) {
  return isAdminRole(roleName);
}

export function canAssignGroupAgents(roleName) {
  return isAdminRole(roleName);
}

export function canAssignGroupChannels(roleName) {
  return isAdminRole(roleName);
}

export function canAssignGroupTeams(roleName) {
  return isAdminRole(roleName);
}

export function canAssignGroupWidgets(roleName) {
  return isAdminRole(roleName);
}

export function canArchiveGroup(roleName) {
  return isAdminRole(roleName);
}

export function canActivateGroup(roleName) {
  return isAdminRole(roleName);
}

export function canBulkManageGroups(roleName) {
  return isAdminRole(roleName);
}
